import {useQueryClient} from '@tanstack/react-query';
import {createFileRoute} from '@tanstack/react-router';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'tolgee';

import {useCancelImageCleanupPipelineMutation} from '~api/automl.mutations';
import {AutoMlQueryKeys, useLatestImageCleanupPipelineQuery} from '~api/automl.queries';
import {pushRoute, replaceRoute, Route as RouteEnum, viewPipelineIdQueryParam} from '~utils/routeUtil';
import {ImageOrderBy} from '~redux/types/images';
import {selectCurrentProjectId} from '~redux/reducers/userReducer';
import {useAppSelector} from '~redux/index';

import {showNotification} from '~components/common/Notification';
import {BaseLayout} from '~components/_layout/BaseLayout';
import {LayoutContent} from '~components/_layout/layoutComponents';
import {ImproveDatasetLoading} from '~components/models/finetune/improve-dataset/ImproveDatasetLoading';
import {ImproveDatasetSuccess} from '~components/models/finetune/improve-dataset/ImproveDatasetSuccess';
import {ModelFooter} from '~components/models/ModelFooter';
import {ModelWorkflowStep} from '~components/models/ModelWorkflowSteps';
import {useCurrentModel} from 'src/contexts/ModelContext';
import {ModelProvider} from 'src/contexts/ModelProvider';

export const Route = createFileRoute('/model/finetune/improve-dataset-loading')({
  component: ImproveDatasetLoadingPage,
});

function ImproveDatasetLoadingPage() {
  return (
    <ModelProvider mode='model-pages'>
      <BaseLayout showBackButton customContentAndFooter>
        <ImproveDatasetLoadingContent />
      </BaseLayout>
    </ModelProvider>
  );
}

function ImproveDatasetLoadingContent() {
  const {t} = useTranslation('common');
  const queryClient = useQueryClient();
  const projectId = useAppSelector(selectCurrentProjectId);
  const {currentModel} = useCurrentModel();
  const [hasFreshData, setHasFreshData] = useState(false);
  const hasSeenRunning = useRef(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Invalidate stale cache on mount so we get a fresh fetch.
  useEffect(() => {
    if (!currentModel?.id) return;
    queryClient
      .invalidateQueries({queryKey: AutoMlQueryKeys.latestImageCleanupPipeline(projectId, currentModel.id)})
      .then(() => setHasFreshData(true));
  }, [queryClient, projectId, currentModel?.id]);

  const cleanupPipelineQuery = useLatestImageCleanupPipelineQuery({modelId: currentModel?.id});
  const cleanupPipeline = cleanupPipelineQuery.data;
  const cleanupJobStatus = cleanupPipeline?.statusData?.job?.status;
  const cleanupPipelineState = cleanupPipeline?.statusData?.pipeline?.status;
  const isCleanupCanceled = cleanupJobStatus === 'canceled' || !!cleanupPipeline?.statusData?.canceled;
  const isCleanupJobFailed = cleanupJobStatus === 'failed';
  // Fall back to pipeline state when job is not yet recorded (early-trigger race window).
  const isCleanupRunning =
    !isCleanupCanceled &&
    (cleanupJobStatus === 'pending' ||
      cleanupJobStatus === 'running' ||
      (cleanupJobStatus === undefined && (cleanupPipelineState === 'pending' || cleanupPipelineState === 'running')));
  const isCleanupComplete = cleanupJobStatus === 'success' || cleanupPipelineState === 'completed';
  const cleanupPipelineTag = cleanupPipeline?.curationPipelineTag;
  const cleanupJobLink = cleanupPipeline?.jobLink;

  useLatestImageCleanupPipelineQuery({
    modelId: currentModel?.id,
    refetchInterval: isCleanupRunning ? 5000 : undefined,
  });

  const cancelCleanupMutation = useCancelImageCleanupPipelineMutation();

  useEffect(() => {
    if (isCleanupRunning) {
      hasSeenRunning.current = true;
    }
  }, [isCleanupRunning]);

  useEffect(() => {
    if (!currentModel?.id) return;
    if (!hasFreshData) return;
    if (cleanupPipelineQuery.isFetching) return;
    if (isCleanupRunning) return;

    if (isCleanupComplete) {
      setShowSuccess(true);
    } else if (isCleanupJobFailed) {
      showNotification({message: t('pipelineFailed'), severity: 'error', id: 'cleanup-pipeline-failed'});
      replaceRoute(RouteEnum.modelFinetune);
    } else if (isCleanupCanceled || !hasSeenRunning.current) {
      replaceRoute(RouteEnum.modelFinetune);
    }
  }, [
    currentModel?.id,
    hasFreshData,
    cleanupPipelineQuery.isFetching,
    isCleanupRunning,
    isCleanupComplete,
    isCleanupCanceled,
    isCleanupJobFailed,
    t,
  ]);

  const handleCancel = () => {
    if (cleanupPipeline?.pipelineId && currentModel?.id) {
      cancelCleanupMutation.mutate({pipelineId: cleanupPipeline.pipelineId, modelId: currentModel.id});
    }
    queryClient.setQueryData(AutoMlQueryKeys.latestImageCleanupPipeline(projectId, currentModel?.id), null);
    pushRoute(RouteEnum.modelFinetune);
  };

  const handleShowResults = () => {
    pushRoute(RouteEnum.modelFinetuneImproveDataset, {
      query: cleanupPipelineTag
        ? {
            orderBy: ImageOrderBy.CURATION_SCORE_DESC,
            curationPipelineTag: cleanupPipelineTag,
            [viewPipelineIdQueryParam]: cleanupPipeline?.pipelineId,
          }
        : undefined,
    });
  };

  return (
    <>
      <LayoutContent sx={{justifyContent: 'center', alignItems: 'center'}}>
        {showSuccess ? (
          <ImproveDatasetSuccess onShowResults={handleShowResults} />
        ) : (
          <ImproveDatasetLoading onCancel={handleCancel} jobLink={cleanupJobLink || undefined} />
        )}
      </LayoutContent>
      <ModelFooter activeStep={ModelWorkflowStep.finetune} />
    </>
  );
}
